import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Tags, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel, Empty, RowSearch } from "@/pages/shared";
import { uid, withUid } from "@/lib/uid";

export const blankSupplier = () => ({
  name: "", contact: "", email: "", phone: "", mobile: "", website: "",
  street: "", number: "", postal_code: "", city: "", country: "",
  vat_number: "", company_number: "", iban: "", bic: "",
  customer_number: "", payment_terms: "", delivery_days: "", notes: "", active: true,
});

const FIELDS = [
  ["name", "sup_name"], ["contact", "sup_contact"], ["email", "email"], ["phone", "phone"],
  ["mobile", "sup_mobile"], ["website", "sup_website"], ["street", "sup_street"], ["number", "sup_number"],
  ["postal_code", "sup_postal"], ["city", "sup_city"], ["country", "country"], ["vat_number", "sup_vat"],
  ["company_number", "sup_company_nr"], ["iban", "sup_iban"], ["bic", "sup_bic"],
  ["customer_number", "sup_customer_nr"], ["payment_terms", "sup_payment_terms"], ["delivery_days", "sup_delivery_days"],
];

export function SupplierFields({ form, setForm }) {
  const { t } = useLang();
  return (
    <div className="grid grid-cols-2 gap-3 max-h-[55vh] overflow-auto cb-scroll pr-1">
      {FIELDS.map(([k, label]) => (
        <div key={k} className="space-y-1">
          <Label className="text-xs">{t(label)}</Label>
          <Input value={form[k] || ""} required={k === "name"} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`supplier-${k}`} />
        </div>
      ))}
      <div className="col-span-2 space-y-1">
        <Label className="text-xs">{t("notes")}</Label>
        <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="supplier-notes" />
      </div>
      <label className="col-span-2 flex items-center gap-2 text-sm">
        <input type="checkbox" className="accent-sky-600" checked={form.active !== false}
          onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="supplier-active" />
        {t("active")}
      </label>
    </div>
  );
}

export default function AdminSuppliers() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(blankSupplier());
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [prices, setPrices] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.get("/suppliers"), api.get("/articles")]);
      setRows(s.data); setArticles(a.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => !s || [r.name, r.city, r.email, r.vat_number].some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const save = async () => {
    setBusy(true);
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      setOpen(false); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/suppliers/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openPrices = (s) => setPrices({
    id: s.id, name: s.name,
    lines: withUid((s.prices || []).flatMap((p) => p.tiers.map((x) => ({ article_id: p.article_id, min_qty: x.min_qty, price: x.price })))),
  });

  const savePrices = async () => {
    const byArticle = {};
    for (const l of prices.lines) {
      if (!l.article_id || !(Number(l.price) >= 0)) continue;
      (byArticle[l.article_id] ||= []).push({ min_qty: Number(l.min_qty) || 1, price: Number(l.price) || 0 });
    }
    setBusy(true);
    try {
      await api.put(`/suppliers/${prices.id}/prices`, { prices: Object.entries(byArticle).map(([article_id, tiers]) => ({ article_id, tiers })) });
      setPrices(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setLine = (i, k, v) => setPrices({ ...prices, lines: prices.lines.map((x, j) => j === i ? { ...x, [k]: v } : x) });

  return (
    <Panel
      title={`${t("admin_suppliers")} (${visible.length}${q ? `/${rows.length}` : ""})`}
      testid="suppliers-panel"
      action={
        <div className="flex items-center gap-2">
          <RowSearch value={q} onChange={setQ} placeholder={t("sup_name")} testid="suppliers-search" />
          <Button onClick={() => { setForm(blankSupplier()); setEditId(null); setOpen(true); }} data-testid="new-supplier-button">
            <Plus className="h-4 w-4 mr-1" />{t("new_supplier")}
          </Button>
        </div>
      }
    >
      <div className="px-5 pt-4 text-sm text-muted-foreground">{t("admin_suppliers_desc")}</div>
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("sup_name")}</TableHead>
              <TableHead>{t("sup_contact")}</TableHead>
              <TableHead>{t("phone")}</TableHead>
              <TableHead>{t("sup_city")}</TableHead>
              <TableHead>{t("sup_vat")}</TableHead>
              <TableHead className="text-right">{t("sup_prices")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((s) => (
              <TableRow key={s.id} data-testid={`supplier-row-${s.id}`} className={s.active === false ? "opacity-50" : ""}>
                <TableCell className="font-semibold text-foreground"><Truck className="inline h-3.5 w-3.5 mr-1.5 text-sky-600" />{s.name}</TableCell>
                <TableCell className="text-muted-foreground">{s.contact}</TableCell>
                <TableCell className="text-muted-foreground">{s.phone || s.mobile}</TableCell>
                <TableCell className="text-muted-foreground">{s.city}</TableCell>
                <TableCell className="font-mono text-xs">{s.vat_number}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openPrices(s)} data-testid={`supplier-prices-${s.id}`}>
                    <Tags className="h-4 w-4 mr-1" />{(s.prices || []).length}
                  </Button>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => { setForm({ ...blankSupplier(), ...s }); setEditId(s.id); setOpen(true); }} data-testid={`edit-supplier-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`delete-supplier-${s.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("edit") : t("new_supplier")}</DialogTitle></DialogHeader>
          <SupplierFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={busy || !form.name.trim()} data-testid="save-supplier-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Afgesproken prijzen per artikel (staffels) */}
      <Dialog open={!!prices} onOpenChange={(o) => !o && setPrices(null)}>
        <DialogContent className="max-w-2xl" data-testid="supplier-prices-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("sup_prices")} — {prices?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("sup_prices_desc")}</p>
          <div className="space-y-2 max-h-[50vh] overflow-auto cb-scroll pr-1">
            {prices?.lines.map((l, i) => (
              <div key={l._uid} className="grid grid-cols-[1fr_6rem_7rem_2rem] gap-2 items-end" data-testid={`price-line-${i}`}>
                <div className="space-y-1">
                  <Label className="text-xs">{t("art_name")}</Label>
                  <Select value={l.article_id} onValueChange={(v) => setLine(i, "article_id", v)}>
                    <SelectTrigger data-testid={`price-article-${i}`}><SelectValue placeholder={t("select_article")} /></SelectTrigger>
                    <SelectContent className="max-h-64">{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">{t("sup_from_qty")}</Label>
                  <Input type="number" min="1" step="1" value={l.min_qty} onChange={(e) => setLine(i, "min_qty", e.target.value)} data-testid={`price-qty-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">{t("sup_unit_price")}</Label>
                  <Input type="number" min="0" step="0.01" value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} data-testid={`price-value-${i}`} /></div>
                <Button variant="ghost" size="sm" onClick={() => setPrices({ ...prices, lines: prices.lines.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setPrices({ ...prices, lines: [...prices.lines, { _uid: uid(), article_id: "", min_qty: 1, price: "" }] })} data-testid="price-add-line">
              <Plus className="h-4 w-4 mr-1" />{t("add_line")}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrices(null)}>{t("cancel")}</Button>
            <Button onClick={savePrices} disabled={busy} data-testid="save-prices-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
